import React, {
  forwardRef,
  RefObject,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { Box, Flex } from '@common/fundamentals'
import { Button } from '@common/material'
import { useModal } from '@common/GlobalModal'
import { localizedStrings } from '@core/strings'
import { Palette } from '@core/theme/color-palette-siteassist'
import {
  ISiteBoxDocument,
  SiteBoxDocument,
} from '@core/entities/SiteBoxDocument'
import { useGetSiteBoxDocument } from '@core/react-query/features/siteBoxDocuments/hooks/useGetSiteBoxDocument'
import { useGetSiteBoxConfiguration } from '@core/react-query/features/siteBoxDocuments/hooks/useGetSiteBoxConfiguration'
import { useRealTimeSiteBoxConfiguration } from '@core/react-query/features/siteBoxDocuments/hooks/useRealTimeSiteBoxConfiguration'
import { PspdfkitComponentRef } from 'components/modal/pdf/PspdfkitComponent'
import { SiteBoxDocumentProperties } from '../documentIntelligence/SiteBoxDocumentProperties'
import { SiteBoxDocumentDrawerForm } from '../documentIntelligence/SiteBoxDocumentDrawerForm'
import {
  DocumentOverallAppraisalView,
  QueryResultAppraisalView,
} from '../documentIntelligence/components'
import { BypassModal } from '../documentIntelligence/modals/BypassModal'
import { DocumentAcceptanceModal } from '../documentIntelligence/modals/DocumentAcceptanceModal'
import { ViewType } from './types'

interface SearchResultInfo {
  current: number
  total: number
}

const usePdfController = (pspdfkitRef?: RefObject<PspdfkitComponentRef>) => {
  const [searchResultInfo, setSearchResultInfo] =
    useState<SearchResultInfo | null>(null)

  const setSearchTerm = useCallback(
    (term: string, pageNumber?: number) => {
      if (term) {
        pspdfkitRef?.current?.setSearchState({ term })
        setTimeout(() => {
          const total = pspdfkitRef?.current?.getSearchResultCount() || 0
          const current = pspdfkitRef?.current?.getCurrentResultIndex() || 0
          setSearchResultInfo({ current: current + 1, total })
        }, 100)
      } else if (pageNumber !== undefined) {
        pspdfkitRef?.current?.clearSearchState()
        pspdfkitRef?.current?.setViewState((viewState) =>
          viewState.set('currentPageIndex', pageNumber - 1),
        )
        setSearchResultInfo(null)
      }
    },
    [pspdfkitRef],
  )

  const clearSearch = useCallback(() => {
    pspdfkitRef?.current?.clearSearchState()
    setSearchResultInfo(null)
  }, [pspdfkitRef])

  const nextResult = useCallback(() => {
    pspdfkitRef?.current?.nextSearchResult()
    const current = pspdfkitRef?.current?.getCurrentResultIndex() || 0
    setSearchResultInfo((prev) =>
      prev ? { ...prev, current: current + 1 } : null,
    )
  }, [pspdfkitRef])

  const previousResult = useCallback(() => {
    pspdfkitRef?.current?.previousSearchResult()
    const current = pspdfkitRef?.current?.getCurrentResultIndex() || 0
    setSearchResultInfo((prev) =>
      prev ? { ...prev, current: current + 1 } : null,
    )
  }, [pspdfkitRef])

  return useMemo(
    () => ({
      setSearchTerm,
      clearSearch,
      nextResult,
      previousResult,
      searchResultInfo,
    }),
    [setSearchTerm, clearSearch, nextResult, previousResult, searchResultInfo],
  )
}

export interface SiteBoxDocumentSidebarRef {
  setView: (view: ViewType) => void
}

export interface SiteBoxDocumentSidebarProps {
  organisationId: number
  projectId: number
  siteBoxDocument: SiteBoxDocument
  pspdfkitRef?: RefObject<PspdfkitComponentRef>
  initialView?: ViewType
  readOnly?: boolean
}

export const SiteBoxDocumentSidebar = forwardRef<
  SiteBoxDocumentSidebarRef,
  SiteBoxDocumentSidebarProps
>(
  (
    {
      organisationId,
      projectId,
      siteBoxDocument: initialSiteBoxDocument,
      pspdfkitRef,
      initialView,
      readOnly,
    },
    ref,
  ) => {
    const { showModal, hideModal } = useModal()
    const documentId = initialSiteBoxDocument.id

    const [view, setView] = useState<ViewType>(
      initialView ?? 'DOCUMENT_PROPERTIES',
    )

    useImperativeHandle(ref, () => ({ setView }))

    const [appraisalStartIndex, setAppraisalStartIndex] = useState(0)

    const pdfController = usePdfController(pspdfkitRef)

    const { data: siteBoxDocument = initialSiteBoxDocument } =
      useGetSiteBoxDocument(
        { documentId },
        { initialData: initialSiteBoxDocument as unknown as ISiteBoxDocument },
      )

    const { data: siteBoxConfiguration } = useGetSiteBoxConfiguration(
      {
        organisationId,
        projectId,
        id: documentId,
      },
      { enabled: !!siteBoxDocument?.documentIntelligence?.config },
    )

    useRealTimeSiteBoxConfiguration(documentId, organisationId, projectId)

    const canBypassReview = siteBoxDocument.canBypassReview()
    const canUserBypassReview = siteBoxDocument.canUserBypassReview()
    const canUserAppraiseDocument = siteBoxDocument.canUserAppraiseDocument()
    const documentEngineTemplateName =
      siteBoxDocument.documentIntelligence?.config?.templates?.[0]?.name ?? ''

    const openBypassModal = () => {
      showModal(
        <BypassModal
          documentId={documentId}
          allowExpiry={siteBoxConfiguration?.config?.allowExpiry ?? false}
          onClose={hideModal}
          onSuccess={() => {
            hideModal()
            setView('DOCUMENT_PROPERTIES')
          }}
          onStartReviewClick={() => {
            setView('SUMMARY')
            hideModal()
          }}
        />,
      )
    }

    const openDocumentAcceptanceModal = () => {
      if (!siteBoxConfiguration) return
      showModal(
        <DocumentAcceptanceModal
          documentId={documentId}
          allowExpiry={siteBoxConfiguration.config?.allowExpiry ?? false}
          results={siteBoxConfiguration.result}
          documentName={siteBoxDocument.name}
          documentDownloadUrl={
            siteBoxDocument.currentVersion?.documentDownloadUrl
          }
          onSuccess={() => {
            hideModal()
            setView('DOCUMENT_PROPERTIES')
          }}
          onClose={hideModal}
        />,
      )
    }

    const showFinishReview =
      !readOnly &&
      view === 'SUMMARY' &&
      (siteBoxConfiguration?.areAllQueriesAppraised() ?? false) &&
      !siteBoxConfiguration?.isDocumentAppraised() &&
      canUserAppraiseDocument

    const showBypassReview =
      !readOnly &&
      view === 'SUMMARY' &&
      canBypassReview &&
      !siteBoxConfiguration?.isDocumentAppraised() &&
      !siteBoxConfiguration?.areAllQueriesAppraised() &&
      canUserBypassReview

    return (
      <Flex flexDirection='column' height='100%' width='100%'>
        <Box flex={1} overflow='auto' px={1}>
          {view === 'DOCUMENT_PROPERTIES' && (
            <SiteBoxDocumentProperties
              siteBoxDocument={siteBoxDocument}
              siteBoxConfiguration={siteBoxConfiguration}
              canBypassReview={canBypassReview}
              canUserBypassReview={canUserBypassReview}
              canUserAppraiseDocument={canUserAppraiseDocument}
              onStartReviewClick={() => setView('SUMMARY')}
              onViewAppraisalClick={() => setView('SUMMARY')}
              onBypassReviewClick={openBypassModal}
              readOnly={readOnly}
            />
          )}

          {view === 'FORM' && (
            <SiteBoxDocumentDrawerForm
              documentId={documentId}
              organisationId={organisationId}
              projectId={projectId}
              onButtonClick={() => setView('DOCUMENT_PROPERTIES')}
            />
          )}

          {view === 'SUMMARY' && (
            <DocumentOverallAppraisalView
              results={siteBoxConfiguration?.result || []}
              onBackClick={() => setView('DOCUMENT_PROPERTIES')}
              setPDFSearchState={pdfController.setSearchTerm}
              clearPDFSearchState={pdfController.clearSearch}
              canUserAppraiseDocument={canUserAppraiseDocument}
              onStartReviewClick={(startIndex) => {
                setAppraisalStartIndex(startIndex)
                setView('QUERY_RESULT_APPRAISAL')
              }}
              documentEngineTemplateName={documentEngineTemplateName}
              readOnly={readOnly}
            />
          )}

          {view === 'QUERY_RESULT_APPRAISAL' && (
            <QueryResultAppraisalView
              documentId={documentId}
              results={siteBoxConfiguration?.result || []}
              isDocumentAppraised={
                (siteBoxConfiguration?.isDocumentAppraised() ?? false) ||
                !!readOnly
              }
              initialIndex={appraisalStartIndex}
              onBackClick={() => setView('SUMMARY')}
              onComplete={() => setView('SUMMARY')}
              setPDFSearchState={pdfController.setSearchTerm}
              clearPDFSearchState={pdfController.clearSearch}
              searchResultInfo={pdfController.searchResultInfo}
              onNextResult={pdfController.nextResult}
              onPreviousResult={pdfController.previousResult}
              canUserAppraiseDocument={canUserAppraiseDocument}
              documentEngineTemplateName={documentEngineTemplateName}
            />
          )}
        </Box>
        {(showFinishReview || showBypassReview) && (
          <Flex
            gap={1}
            p={1}
            borderTop='1px solid lightgrey'
            justifyContent='flex-end'
          >
            {showFinishReview && (
              <Button
                variant='contained'
                sx={{ bgcolor: Palette.Violet[500] }}
                onClick={openDocumentAcceptanceModal}
              >
                {localizedStrings.finishReview}
              </Button>
            )}
            {showBypassReview && (
              <Button
                variant='contained'
                sx={{ bgcolor: Palette.Violet[500] }}
                onClick={openBypassModal}
              >
                {localizedStrings.bypassReview}
              </Button>
            )}
          </Flex>
        )}
      </Flex>
    )
  },
)
